import React, { useState, useEffect } from 'react';
import { Button, Table, Space, Modal, Tag, message, Empty, Tooltip, Badge } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, ThunderboltOutlined } from '@ant-design/icons';
import FunctionForm from './FunctionForm';
import apiClient from '../api';

const FunctionManager = ({ loading: initialLoading, onUpdate }) => {
  const [functions, setFunctions] = useState([]);
  const [loading, setLoading] = useState(initialLoading);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingFunction, setEditingFunction] = useState(null);
  const [viewingFunction, setViewingFunction] = useState(null);
  const [testingFunction, setTestingFunction] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);

  useEffect(() => {
    loadFunctions();
  }, []);

  const loadFunctions = async () => {
    setLoading(true);
    try {
      const response = await apiClient.get('/custom-functions');
      if (response.data.success) {
        setFunctions(response.data.functions);
      }
    } catch (error) {
      message.error('Failed to load custom functions');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingFunction(null);
    setIsModalVisible(true);
  };

  const handleEdit = (func) => {
    setEditingFunction(func);
    setIsModalVisible(true);
  };

  const handleView = (func) => {
    setViewingFunction(func);
  };

  const handleTest = (func) => {
    setTestingFunction(func);
  };

  // Open Ant Design modal to confirm delete
  const handleDelete = (func) => {
    if (!func) return;
    console.log('[FunctionManager] Delete clicked for record:', func);
    setDeleteTarget(func);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    const id = deleteTarget._id || deleteTarget.functionId;
    if (!id) return;

    try {
      console.log('[FunctionManager] Confirming delete for id:', id);
      const response = await apiClient.delete(
        `/custom-functions/${encodeURIComponent(id)}`
      );
      if (response.data.success) {
        message.success('Function deleted successfully');
        setDeleteTarget(null);
        loadFunctions();
        if (onUpdate) onUpdate();
      } else {
        message.error(response.data.message || 'Failed to delete function');
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Failed to delete function');
      console.error(error);
    }
  };

  const handleFormSubmit = async (values) => {
    try {
      let response;
      if (editingFunction) {
        const id = editingFunction._id || editingFunction.functionId;
        response = await apiClient.put(`/custom-functions/${encodeURIComponent(id)}`, values);
      } else {
        response = await apiClient.post(`/custom-functions`, values);
      }

      if (response.data.success) {
        message.success(`Function ${editingFunction ? 'updated' : 'created'} successfully`);
        setIsModalVisible(false);
        loadFunctions();
        if (onUpdate) onUpdate();
      } else {
        message.error(response.data.message || 'Operation failed');
      }
    } catch (error) {
      message.error(error.response?.data?.message || 'Operation failed');
      console.error(error);
    }
  };

  const handleTestFunction = async (testData) => {
    try {
      const id = testingFunction._id || testingFunction.functionId;
      const response = await apiClient.post(
        `/custom-functions/${encodeURIComponent(id)}/test`,
        { testParameters: testData }
      );

      if (response.data.success) {
        Modal.success({
          title: 'Function Test Successful',
          content: (
            <div>
              <p><strong>Status:</strong> {response.data.status}</p>
              <div style={{ 
                marginTop: 12, 
                padding: 12, 
                background: '#f5f5f5', 
                borderRadius: 6,
                maxHeight: 300,
                overflow: 'auto'
              }}>
                <pre style={{ margin: 0, fontSize: 12 }}>
                  {JSON.stringify(response.data.result, null, 2)}
                </pre>
              </div>
            </div>
          ),
          width: 600
        });
      }
    } catch (error) {
      Modal.error({
        title: 'Function Test Failed',
        content: (
          <div>
            <p><strong>Status:</strong> {error.response?.data?.status || 'Error'}</p>
            <div style={{ 
              marginTop: 12, 
              padding: 12, 
              background: '#fff2f0', 
              borderRadius: 6,
              maxHeight: 300,
              overflow: 'auto'
            }}>
              <pre style={{ margin: 0, fontSize: 12, color: '#cf1322' }}>
                {JSON.stringify(error.response?.data?.error || error.message, null, 2)}
              </pre>
            </div>
          </div>
        ),
        width: 600
      });
    }
  };

  const columns = [
    {
      title: 'Function ID',
      dataIndex: 'functionId',
      key: 'functionId',
      width: 200,
      render: (text) => <Tag color="purple">{text}</Tag>,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 200,
      render: (text) => <strong>{text}</strong>,
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      ellipsis: true,
      render: (text) => (
        <Tooltip title={text}>
          <span>{text.substring(0, 50)}{text.length > 50 ? '...' : ''}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Method',
      dataIndex: 'httpMethod',
      key: 'httpMethod',
      width: 100,
      render: (method) => {
        const colors = {
          GET: 'blue',
          POST: 'green',
          PUT: 'orange',
          PATCH: 'cyan',
          DELETE: 'red'
        };
        return <Tag color={colors[method]}>{method}</Tag>;
      },
    },
    {
      title: 'Auth',
      dataIndex: 'authType',
      key: 'authType',
      width: 120,
      render: (authType) => {
        if (authType === 'none') return <Tag>None</Tag>;
        return <Tag color="gold">{authType.replace('_', ' ').toUpperCase()}</Tag>;
      },
    },
    {
      title: 'Parameters',
      dataIndex: 'parameters',
      key: 'parameters',
      width: 100,
      render: (params) => (
        <Badge count={params?.length || 0} style={{ backgroundColor: '#52c41a' }} />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 200,
      fixed: 'right',
      render: (_, record) => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button 
            type="text" 
            icon={<ThunderboltOutlined />}
            onClick={() => handleTest(record)}
            title="Test"
          />
          <Button 
            type="text" 
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
            title="View"
          />
          <Button 
            type="text" 
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            style={{ color: '#1890ff' }}
            title="Edit"
          />
          <Button 
            type="text" 
            danger 
            icon={<DeleteOutlined />}
            onClick={() => handleDelete(record)}
            title="Delete"
          />
        </div>
      ),
    },
  ];

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <h2 style={{ margin: 0 }}>⚡ Custom Functions</h2>
          <p style={{ color: '#666', margin: '4px 0 0 0' }}>
            Create custom API functions with HTTP endpoints, authentication, and parameters
          </p>
        </div>
        <Button 
          type="primary" 
          icon={<PlusOutlined />} 
          size="large"
          onClick={handleCreate}
        >
          Create Function
        </Button>
      </div>

      <Table 
        columns={columns} 
        dataSource={functions}
        rowKey="functionId"
        loading={loading}
        pagination={{ pageSize: 10 }}
        scroll={{ x: 1200 }}
        locale={{
          emptyText: (
            <Empty 
              description="No custom functions yet. Create your first function!"
              image={Empty.PRESENTED_IMAGE_SIMPLE}
            />
          )
        }}
      />

      {/* Delete confirmation modal */}
      <Modal
        title="Delete Custom Function"
        open={!!deleteTarget}
        onCancel={() => setDeleteTarget(null)}
        onOk={handleConfirmDelete}
        okText="Delete"
        okButtonProps={{ danger: true }}
        destroyOnClose
      >
        <p>Are you sure you want to delete this function? This action cannot be undone.</p>
        {deleteTarget && (
          <p>
            <strong>Function ID:</strong>{' '}
            <Tag color="purple" style={{ marginLeft: 4 }}>
              {deleteTarget.functionId || deleteTarget._id}
            </Tag>
          </p>
        )}
      </Modal>

      <Modal
        title={editingFunction ? '✏️ Edit Function' : '➕ Create New Function'}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={900}
        destroyOnClose
      >
        <FunctionForm 
          initialValues={editingFunction}
          onSubmit={handleFormSubmit}
          onCancel={() => setIsModalVisible(false)}
        />
      </Modal>

      <Modal
        title="👀 Function Details"
        open={!!viewingFunction}
        onCancel={() => setViewingFunction(null)}
        footer={[
          <Button key="close" onClick={() => setViewingFunction(null)}>
            Close
          </Button>,
          <Button 
            key="edit" 
            type="primary" 
            onClick={() => {
              setViewingFunction(null);
              handleEdit(viewingFunction);
            }}
          >
            Edit Function
          </Button>,
        ]}
        width={700}
      >
        {viewingFunction && (
          <div>
            <div style={{ marginBottom: 16 }}>
              <strong>Function ID:</strong>
              <Tag color="purple" style={{ marginLeft: 8 }}>{viewingFunction.functionId}</Tag>
            </div>
            <div style={{ marginBottom: 16 }}>
              <strong>Name:</strong> {viewingFunction.name}
            </div>
            <div style={{ marginBottom: 16 }}>
              <strong>Description:</strong> {viewingFunction.description}
            </div>
            <div style={{ marginBottom: 16 }}>
              <strong>HTTP Method:</strong> <Tag color="blue">{viewingFunction.httpMethod}</Tag>
            </div>
            <div style={{ marginBottom: 16 }}>
              <strong>Endpoint:</strong>
              <div style={{ 
                marginTop: 8, 
                padding: 12, 
                background: '#f5f5f5', 
                borderRadius: 6,
                fontFamily: 'monospace',
                fontSize: '13px'
              }}>
                {viewingFunction.endpoint}
              </div>
            </div>
            <div style={{ marginBottom: 16 }}>
              <strong>Authentication:</strong> <Tag color="gold">{viewingFunction.authType}</Tag>
            </div>
            {viewingFunction.parameters && viewingFunction.parameters.length > 0 && (
              <div>
                <strong>Parameters ({viewingFunction.parameters.length}):</strong>
                <div style={{ marginTop: 8 }}>
                  {viewingFunction.parameters.map((param, idx) => (
                    <div key={idx} style={{ 
                      padding: 8, 
                      marginBottom: 8, 
                      background: '#fafafa', 
                      borderRadius: 4,
                      border: '1px solid #f0f0f0'
                    }}>
                      <strong>{param.name}</strong> ({param.type}) 
                      {param.required && <Tag color="red" style={{ marginLeft: 8 }}>Required</Tag>}
                      <Tag style={{ marginLeft: 8 }}>{param.location}</Tag>
                      <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                        {param.description}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </Modal>

      <Modal
        title="⚡ Test Function"
        open={!!testingFunction}
        onCancel={() => setTestingFunction(null)}
        footer={null}
        width={600}
      >
        {testingFunction && (
          <div>
            <p>Test your function by providing parameter values:</p>
            <div style={{ marginTop: 16 }}>
              {testingFunction.parameters && testingFunction.parameters.length > 0 ? (
                <FunctionTestForm 
                  parameters={testingFunction.parameters}
                  onTest={handleTestFunction}
                  onCancel={() => setTestingFunction(null)}
                />
              ) : (
                <div>
                  <p>This function has no parameters.</p>
                  <Button type="primary" onClick={() => handleTestFunction({})}>
                    Run Test
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

// Mini form for testing functions
const FunctionTestForm = ({ parameters, onTest, onCancel }) => {
  const [testData, setTestData] = useState({});

  const handleChange = (paramName, value) => {
    setTestData({ ...testData, [paramName]: value });
  };

  const handleSubmit = () => {
    onTest(testData);
  };

  return (
    <div>
      {parameters.map((param) => (
        <div key={param.name} style={{ marginBottom: 16 }}>
          <label style={{ display: 'block', marginBottom: 4, fontWeight: 600 }}>
            {param.name} {param.required && <span style={{ color: 'red' }}>*</span>}
          </label>
          <input
            type="text"
            placeholder={param.description || `Enter ${param.name}`}
            onChange={(e) => handleChange(param.name, e.target.value)}
            style={{
              width: '100%',
              padding: '8px 12px',
              border: '1px solid #d9d9d9',
              borderRadius: '6px',
              fontSize: '14px'
            }}
          />
        </div>
      ))}
      <div style={{ marginTop: 24, textAlign: 'right' }}>
        <Button onClick={onCancel} style={{ marginRight: 8 }}>
          Cancel
        </Button>
        <Button type="primary" onClick={handleSubmit}>
          Run Test
        </Button>
      </div>
    </div>
  );
};

export default FunctionManager;

